import { useMemo } from 'react';
import { useAsyncSection, type AsyncSectionState } from '../../hooks/useAsyncSection';
import type { WesFilterSelection } from './useWesPalletStagingFilters';
import { fetchWesExport, fetchWesLocationDetail, type WesApiDetailPage } from './wesPalletStagingApi';
import { buildWesReadRequest, dimensionSortOf, sortRowsByDimension } from './wesPalletStagingRequest';
import type { WesDimKey, WesDisplayMode, WesTableSort } from './wesPalletStagingTable';

interface UseWesLocationDetailOptions {
  applied: WesFilterSelection;
  periodWeeks: any[];
  dimensions: WesDimKey[];
  sort: WesTableSort | null;
  mode: WesDisplayMode;
  page: number;
  pageSize?: number;
}

// Metric sorts and paging come from the API; a dimension sort loads every group, sorts them all, then pages locally.
export function useWesLocationDetail({ applied, periodWeeks, dimensions, sort, mode, page, pageSize }: UseWesLocationDetailOptions): AsyncSectionState<WesApiDetailPage> {
  const dimensionSort = dimensionSortOf(sort, dimensions);
  const built = useMemo(
    () =>
      buildWesReadRequest(
        applied,
        periodWeeks,
        dimensionSort ? { dimensions, displayMode: mode } : { dimensions, sort, displayMode: mode, page, pageSize },
      ),
    [applied, periodWeeks, dimensions, sort, mode, page, pageSize, dimensionSort],
  );
  const key = `${built.ok ? JSON.stringify(built.request) : built.reason}|${dimensionSort ? `${dimensionSort.key}:${dimensionSort.dir}:${page}:${pageSize}` : ''}`;

  const section = useAsyncSection(
    async () => {
      if (!built.ok) throw new Error('invalid');
      if (!dimensionSort) return fetchWesLocationDetail(built.request);
      const all = await fetchWesExport(built.request);
      const size = pageSize ?? all.rows.length;
      const sorted = sortRowsByDimension(all.rows, dimensionSort.key, dimensionSort.dir);
      return {
        rows: sorted.slice(page * size, (page + 1) * size),
        page,
        size,
        totalGroups: all.totalGroups,
        totalPages: size > 0 ? Math.ceil(all.totalGroups / size) : 1,
        totals: all.totals,
      };
    },
    [key],
    { enabled: built.ok, toFriendlyError: () => 'Unable to load this section. Please try again.' },
  );

  if (!built.ok) return { data: null, loading: false, error: built.reason, retry: () => undefined };
  return section;
}
